import React from "react";
import { Button, Result, Spin } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { usePermissions } from "./PermissionProvider";
import {
    ACCESS_PROTECTED,
    ACCESS_PUBLIC,
    resolveRouteAccess,
} from "./permissionMap";

const IS_DEV = process.env.NODE_ENV !== "production";
const HOME_PATH = "/dashboard-page";

const centered = {
    minHeight: "45vh",
    display: "grid",
    placeItems: "center",
};

/**
 * Route-level authorisation.
 *
 * Resolution order:
 *   1. an explicit `requirement` prop
 *   2. the `path` prop (the route pattern, e.g. "/ReviewNonMetricSpace/:extension")
 *   3. the live pathname from the router
 *
 * A path that is neither mapped in ROUTE_PERMISSIONS nor listed in
 * PUBLIC_ROUTES is refused. Forgetting to map a new page therefore produces a
 * visible error for the developer instead of an invisible hole for everyone.
 */
function PermissionGuard({ requirement, path, children }) {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { loading, error, canAccess, refreshPermissions } = usePermissions();

    const resolved = React.useMemo(() => {
        if (requirement != null) {
            return { status: ACCESS_PROTECTED, requirement };
        }
        return resolveRouteAccess(path || pathname);
    }, [requirement, path, pathname]);

    if (resolved.status === ACCESS_PUBLIC) return children;

    if (loading) {
        return (
            <div style={centered}>
                <Spin size="large" tip="در حال بررسی سطح دسترسی..." />
            </div>
        );
    }

    if (error) {
        return (
            <Result
                status="error"
                title="دریافت سطح دسترسی ناموفق بود"
                subTitle="برای حفظ امنیت، تا زمان دریافت مجدد مجوزها این صفحه نمایش داده نمی‌شود."
                extra={
                    <Button type="primary" onClick={refreshPermissions}>
                        تلاش دوباره
                    </Button>
                }
            />
        );
    }

    // Unmapped route: deny, and say why while developing.
    if (resolved.status !== ACCESS_PROTECTED) {
        if (IS_DEV) {
            console.error(
                `[permissions] "${resolved.normalizedPath ?? path ?? pathname}" has no entry in ` +
                "ROUTE_PERMISSIONS and is not in PUBLIC_ROUTES, so access is denied. " +
                "Add it to whichever list is correct."
            );
        }

        return (
            <Result
                status="403"
                title="دسترسی غیرمجاز"
                subTitle="این صفحه پیکربندی سطح دسترسی ندارد."
                extra={
                    <Button
                        type="primary"
                        onClick={() => navigate(HOME_PATH, { replace: true })}
                    >
                        بازگشت به صفحه نخست
                    </Button>
                }
            />
        );
    }

    if (!canAccess(resolved.requirement)) {
        return (
            <Result
                status="403"
                title="دسترسی غیرمجاز"
                subTitle="حساب شما اجازه استفاده از این بخش را ندارد."
                extra={
                    <Button
                        type="primary"
                        onClick={() => navigate(HOME_PATH, { replace: true })}
                    >
                        بازگشت به صفحه نخست
                    </Button>
                }
            />
        );
    }

    return children;
}

export default PermissionGuard;
